import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Title,
  Paper,
  Grid,
  Select,
  SegmentedControl,
  Button,
  Text,
  Group,
  Card,
  Stack,
  Badge,
  Alert,
  Box,
  ActionIcon,
  Tooltip,
  Divider,
  ScrollArea,
  Modal,
  useMantineColorScheme,
} from '@mantine/core';
import {
  IconInfoCircle,
  IconHistory,
  IconPlayerPlayFilled,
  IconSparkles,
  IconRefresh,
  IconDice5,
} from '@tabler/icons-react';
import { Tema } from '../services/temaService';
import { Especialidad } from '../services/especialidadService';
import { FlashcardEstadisticasDTO } from '../Models/Flashcard';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { especialidadesQuery, temasQuery, flashcardStatsQuery } from '../queries/appQueries';
import { flashcardService } from '../services/flashcardService';
import PageSkeleton from '../components/ui/PageSkeleton';

type Modo = 'NUEVAS' | 'REPASO' | 'MEZCLA';

const FlashcardsPage: React.FC = () => {
  const navigate = useNavigate();
  const { colorScheme } = useMantineColorScheme();
  const dark = colorScheme === 'dark';

  // ---------------- Design tokens (mismo lenguaje que Perfil / Simulador) ----------------
  const cardStyle: React.CSSProperties = {
    backgroundColor: dark ? 'rgba(30, 41, 59, 0.7)' : 'rgba(247, 243, 238, 0.95)',
    border: `1px solid ${dark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(221, 216, 209, 0.8)'}`,
    boxShadow: 'none',
  };
  const textColor = dark ? '#e2e8f0' : '#2d2a26';
  const mutedColor = dark ? '#94a3b8' : '#8c8478';
  // Paleta pastel apagada para los números de "Tu progreso" (consistente con EstadisticasPage).
  const pastelBlue = dark ? '#8aa9d6' : '#7c98c6';
  const pastelGreen = dark ? '#86b8a6' : '#6f9d8b';
  const pastelAmber = dark ? '#cdb487' : '#b89a63';
  const pastelViolet = dark ? '#a99bd1' : '#9385c0';
  const primaryButtonStyles = {
    root: {
      backgroundColor: dark ? '#e2e8f0' : '#1f2937',
      color: dark ? '#1f2937' : '#ffffff',
      '&:hover': { backgroundColor: dark ? '#cbd5e1' : '#111827' },
      '&:disabled': {
        backgroundColor: dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
        color: dark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
      },
    },
  };

  const queryClient = useQueryClient();
  const [reiniciando, setReiniciando] = useState(false);
  const [confirmReinicioAbierto, setConfirmReinicioAbierto] = useState(false);

  // ---------------- Datos cacheados ----------------
  const especialidadesQ = useQuery(especialidadesQuery());
  const temasQ = useQuery(temasQuery());
  const especialidades: Especialidad[] = especialidadesQ.data ?? [];
  const todosLosTemas: Tema[] = useMemo(() => temasQ.data ?? [], [temasQ.data]);

  const [temasFiltrados, setTemasFiltrados] = useState<Tema[]>([]);
  const [selectedEspecialidadId, setSelectedEspecialidadId] = useState<number | null>(null);
  const [selectedTemaId, setSelectedTemaId] = useState<number | null>(null);
  const [modo, setModo] = useState<Modo>('NUEVAS');
  const [cantidad, setCantidad] = useState<string>('20');
  const [error, setError] = useState<string | null>(null);

  const getUserId = (): number => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      const user = JSON.parse(userStr);
      return user.id;
    }
    return 0;
  };
  const usuarioId = getUserId();

  const statsQ = useQuery(flashcardStatsQuery(usuarioId));
  const estadisticas: FlashcardEstadisticasDTO | undefined = statsQ.data;
  const loading = statsQ.isLoading;
  const isError = statsQ.isError;

  useEffect(() => {
    if (usuarioId <= 0) setError('Error: Usuario no válido');
    else if (isError) setError('Error al cargar los datos. Por favor, intenta de nuevo.');
  }, [usuarioId, isError]);

  useEffect(() => {
    if (selectedEspecialidadId) {
      setTemasFiltrados(
        (todosLosTemas as Tema[]).filter((t: Tema) => t.especialidadId === selectedEspecialidadId)
      );
    } else {
      setTemasFiltrados([]);
      setSelectedTemaId(null);
    }
  }, [selectedEspecialidadId, todosLosTemas]);

  const handleComenzarEstudio = () => {
    if (!selectedEspecialidadId) {
      setError('Debes seleccionar una especialidad');
      return;
    }
    const params = new URLSearchParams();
    params.append('especialidadId', selectedEspecialidadId.toString());
    params.append('usuarioId', usuarioId.toString());
    params.append('modo', modo);
    if (cantidad !== 'TODAS') params.append('limit', cantidad);
    params.append('shuffle', 'true');
    if (selectedTemaId) params.append('temaId', selectedTemaId.toString());
    navigate(`/estudiante/flashcards/estudiar?${params.toString()}`);
  };

  const getEspecialidadNombre = (id: number) =>
    especialidades.find((e) => e.id === id)?.nombre || '';
  const getTemaNombre = (id: number) => todosLosTemas.find((t) => t.id === id)?.nombre || '';

  // ---------------- Historial ----------------
  const historialQ = useQuery({
    queryKey: ['flashcardHistorial', usuarioId],
    queryFn: () => flashcardService.obtenerHistorial(usuarioId, 20),
    enabled: usuarioId > 0,
    staleTime: 60 * 1000,
  });
  const historial =
    (historialQ.data as Array<{
      flashcardId: number;
      pregunta: string;
      especialidadNombre?: string;
      temaNombre?: string;
      vecesEstudiado: number;
      ultimaVezEstudiado: string;
    }> | undefined) ?? [];

  const tiempoRelativo = (iso: string) => {
    const d = new Date(iso);
    const diff = Date.now() - d.getTime();
    const min = Math.floor(diff / 60000);
    if (min < 1) return 'ahora';
    if (min < 60) return `hace ${min} min`;
    const h = Math.floor(min / 60);
    if (h < 24) return `hace ${h} h`;
    const days = Math.floor(h / 24);
    if (days < 7) return `hace ${days} d`;
    return d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short' });
  };

  const handleRepasarHistorial = () => {
    if (historial.length === 0) return;
    const ids = historial.map((h) => h.flashcardId).join(',');
    const params = new URLSearchParams();
    params.append('usuarioId', usuarioId.toString());
    params.append('ids', ids);
    params.append('shuffle', 'true');
    navigate(`/estudiante/flashcards/estudiar?${params.toString()}`);
  };

  const handleRepasarUna = (id: number) => {
    const params = new URLSearchParams();
    params.append('usuarioId', usuarioId.toString());
    params.append('ids', id.toString());
    navigate(`/estudiante/flashcards/estudiar?${params.toString()}`);
  };

  const handleReiniciarProgreso = async () => {
    setReiniciando(true);
    try {
      const reiniciadas = await flashcardService.reiniciarProgreso(usuarioId);
      // Refrescar estadísticas e historial para reflejar el reinicio
      await queryClient.invalidateQueries({ queryKey: ['flashcardStats', usuarioId] });
      await queryClient.invalidateQueries({ queryKey: ['flashcardHistorial', usuarioId] });
      setConfirmReinicioAbierto(false);
      notifications.show({
        title: 'Flashcards reiniciadas',
        message: `Se reiniciaron ${reiniciadas} tarjeta(s). Ahora aparecen como nuevas.`,
        color: 'green',
      });
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'No se pudo reiniciar el progreso. Inténtalo de nuevo.',
        color: 'red',
      });
    } finally {
      setReiniciando(false);
    }
  };

  // Styles compartidos para inputs
  const inputStyles = {
    root: { textAlign: 'left' as const },
    label: {
      color: textColor,
      fontFamily: 'Inter, sans-serif',
      fontWeight: 500,
      fontSize: '14px',
      textAlign: 'left' as const,
    },
    input: {
      backgroundColor: dark ? 'rgba(255, 255, 255, 0.08)' : '#ffffff',
      border: `1.5px solid ${dark ? 'rgba(255, 255, 255, 0.18)' : 'rgba(221, 216, 209, 0.8)'}`,
      borderRadius: '12px',
      fontSize: '15px',
      fontWeight: 500,
      color: textColor,
      padding: '10px 14px',
      height: '42px',
    },
    dropdown: {
      backgroundColor: dark ? 'rgba(30, 41, 59, 0.98)' : '#ffffff',
      border: `1px solid ${dark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(221, 216, 209, 0.8)'}`,
      borderRadius: '12px',
    },
  };

  if (loading) {
    return <PageSkeleton variant="grid" count={4} />;
  }

  if (error) {
    return (
      <Box style={{ padding: '24px' }}>
        <Alert icon={<IconInfoCircle size={16} />} title="Error" color="red" variant="light">
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      style={{
        padding: '16px 24px',
        minHeight: '100vh',
        overflow: 'hidden',
        overflowY: 'auto',
        backgroundColor: dark ? 'rgba(15, 23, 42, 0.5)' : 'rgba(247, 243, 238, 0.3)',
      }}
    >
      {/* Header alineado al del Simulador */}
      <Box mb="md" ta="left">
        <Group gap={8} align="center" mb={4}>
          <Title
            order={2}
            size="1.3rem"
            fw={600}
            ta="left"
            style={{
              color: dark ? '#e2e8f0' : '#1e293b',
              fontFamily: 'Space Grotesk, Inter, sans-serif',
              margin: 0,
            }}
          >
            Anki
          </Title>
        </Group>
        <Text
          size="xs"
          ta="left"
          style={{ color: textColor, fontFamily: 'Inter, sans-serif' }}
        >
          Repasa contenido médico con nuestro sistema de tarjetas de estudio
        </Text>
      </Box>

      <Grid>
        {/* -------- Panel principal de configuración -------- */}
        <Grid.Col span={{ base: 12, md: 8 }}>
          <Paper radius="lg" p="lg" style={cardStyle}>
            <Title
              order={4}
              ta="left"
              mb="md"
              style={{
                fontFamily: 'Space Grotesk, Inter, sans-serif',
                color: textColor,
                fontWeight: 600,
              }}
            >
              Configurar sesión de estudio
            </Title>

            <Stack gap="md">
              <Select
                label="Especialidad"
                placeholder="Selecciona una especialidad"
                value={selectedEspecialidadId ? selectedEspecialidadId.toString() : null}
                onChange={(value) => {
                  setSelectedEspecialidadId(value ? parseInt(value) : null);
                  setError(null);
                }}
                data={especialidades.map((esp) => ({
                  value: esp.id.toString(),
                  label: esp.nombre,
                }))}
                required
                searchable
                styles={inputStyles}
              />

              <Select
                label="Tema (opcional)"
                placeholder="Selecciona un tema específico"
                value={selectedTemaId ? selectedTemaId.toString() : null}
                onChange={(value) => setSelectedTemaId(value ? parseInt(value) : null)}
                data={temasFiltrados.map((tema) => ({
                  value: tema.id.toString(),
                  label: tema.nombre,
                }))}
                disabled={!selectedEspecialidadId}
                searchable
                clearable
                styles={inputStyles}
              />

              {/* Modo de estudio */}
              <Box>
                <Text
                  size="sm"
                  fw={500}
                  mb={8}
                  ta="left"
                  style={{ color: textColor, fontFamily: 'Inter, sans-serif' }}
                >
                  Modo de estudio
                </Text>
                <SegmentedControl
                  fullWidth
                  value={modo}
                  onChange={(v) => setModo(v as Modo)}
                  data={[
                    {
                      value: 'NUEVAS',
                      label: (
                        <Group gap={6} justify="center" wrap="nowrap">
                          <IconSparkles size={14} />
                          <span>Nuevas</span>
                        </Group>
                      ),
                    },
                    {
                      value: 'REPASO',
                      label: (
                        <Group gap={6} justify="center" wrap="nowrap">
                          <IconRefresh size={14} />
                          <span>Repaso</span>
                        </Group>
                      ),
                    },
                    {
                      value: 'MEZCLA',
                      label: (
                        <Group gap={6} justify="center" wrap="nowrap">
                          <IconDice5 size={14} />
                          <span>Mezcla</span>
                        </Group>
                      ),
                    },
                  ]}
                  styles={{
                    root: {
                      backgroundColor: dark ? 'rgba(255,255,255,0.05)' : 'rgba(45,42,38,0.05)',
                      borderRadius: 10,
                    },
                    indicator: {
                      backgroundColor: dark ? 'rgba(255,255,255,0.12)' : '#ffffff',
                      boxShadow: dark ? 'none' : '0 1px 2px rgba(0,0,0,0.06)',
                    },
                    label: { color: textColor, fontWeight: 500 },
                  }}
                />
                <Text size="xs" mt={6} ta="left" style={{ color: mutedColor }}>
                  {modo === 'NUEVAS' && 'Solo tarjetas que aún no has marcado como estudiadas.'}
                  {modo === 'REPASO' && 'Solo tarjetas que ya marcaste como estudiadas (para repasar).'}
                  {modo === 'MEZCLA' && 'Todas las tarjetas, mezcladas (nuevas y estudiadas).'}
                </Text>
              </Box>

              <Select
                label="Cantidad de tarjetas"
                value={cantidad}
                onChange={(v) => setCantidad(v || '20')}
                data={[
                  { value: '10', label: '10 tarjetas' },
                  { value: '20', label: '20 tarjetas' },
                  { value: '50', label: '50 tarjetas' },
                  { value: '100', label: '100 tarjetas' },
                  { value: 'TODAS', label: 'Todas' },
                ]}
                styles={inputStyles}
              />

              {/* Resumen */}
              {selectedEspecialidadId && (
                <Card withBorder radius="md" p="sm" style={cardStyle}>
                  <Text size="xs" fw={500} mb={6} style={{ color: mutedColor }}>
                    Resumen
                  </Text>
                  <Group gap={6}>
                    <Badge variant="light" color="gray">
                      {getEspecialidadNombre(selectedEspecialidadId)}
                    </Badge>
                    {selectedTemaId && (
                      <Badge variant="light" color="gray">
                        {getTemaNombre(selectedTemaId)}
                      </Badge>
                    )}
                    <Badge variant="light" color="gray">
                      {modo === 'NUEVAS' ? 'Nuevas' : modo === 'REPASO' ? 'Repaso' : 'Mezcla'}
                    </Badge>
                    <Badge variant="light" color="gray">
                      {cantidad === 'TODAS' ? 'Todas' : `${cantidad} tarjetas`}
                    </Badge>
                  </Group>
                </Card>
              )}

              <Button
                size="md"
                radius="md"
                onClick={handleComenzarEstudio}
                disabled={!selectedEspecialidadId}
                fullWidth
                styles={primaryButtonStyles}
              >
                Comenzar a estudiar
              </Button>
            </Stack>
          </Paper>
        </Grid.Col>

        {/* -------- Panel lateral: progreso + historial -------- */}
        <Grid.Col span={{ base: 12, md: 4 }}>
          <Paper radius="lg" p="lg" style={cardStyle}>
            <Title
              order={5}
              ta="left"
              mb="md"
              style={{
                fontFamily: 'Space Grotesk, Inter, sans-serif',
                color: textColor,
                fontWeight: 600,
              }}
            >
              Tu progreso
            </Title>

            {estadisticas && (
              <Stack gap={6} mb="md">
                <Group justify="space-between">
                  <Text size="sm" style={{ color: mutedColor }}>Total</Text>
                  <Text size="sm" fw={600} style={{ color: pastelBlue }}>
                    {estadisticas.totalFlashcards}
                  </Text>
                </Group>
                <Group justify="space-between">
                  <Text size="sm" style={{ color: mutedColor }}>Estudiadas</Text>
                  <Text size="sm" fw={600} style={{ color: pastelGreen }}>
                    {estadisticas.flashcardsEstudiadas}
                  </Text>
                </Group>
                <Group justify="space-between">
                  <Text size="sm" style={{ color: mutedColor }}>Pendientes</Text>
                  <Text size="sm" fw={600} style={{ color: pastelAmber }}>
                    {estadisticas.flashcardsPendientes}
                  </Text>
                </Group>
                <Divider my={4} color={dark ? 'rgba(255,255,255,0.08)' : 'rgba(45,42,38,0.08)'} />
                <Group justify="space-between">
                  <Text size="sm" fw={500} style={{ color: textColor }}>Progreso</Text>
                  <Text size="sm" fw={700} style={{ color: pastelViolet }}>
                    {estadisticas.porcentajeCompletado.toFixed(1)}%
                  </Text>
                </Group>
              </Stack>
            )}

            <Button
              onClick={() => navigate('/estudiante/flashcards/estadisticas')}
              fullWidth
              size="sm"
              radius="md"
              styles={primaryButtonStyles}
            >
              Ver estadísticas detalladas
            </Button>
          </Paper>

          {/* Historial */}
          <Paper radius="lg" p="lg" style={cardStyle} mt="md">
            <Group justify="space-between" mb="md" wrap="nowrap">
              <Group gap={6} wrap="nowrap">
                <IconHistory size={16} color={mutedColor} />
                <Title
                  order={5}
                  style={{
                    fontFamily: 'Space Grotesk, Inter, sans-serif',
                    color: textColor,
                    fontWeight: 600,
                    margin: 0,
                  }}
                >
                  Últimas estudiadas
                </Title>
              </Group>
              {historial.length > 0 && (
                <Group gap="xs">
                  <Tooltip label="Repasar todas las del historial">
                    <Button
                      size="xs"
                      radius="md"
                      leftSection={<IconPlayerPlayFilled size={12} />}
                      onClick={handleRepasarHistorial}
                      styles={primaryButtonStyles}
                    >
                      Repasar
                    </Button>
                  </Tooltip>
                  <Tooltip label="Reiniciar todas las tarjetas estudiadas (vuelven a ser nuevas)">
                    <Button
                      size="xs"
                      radius="md"
                      variant="light"
                      color="red"
                      leftSection={<IconRefresh size={12} />}
                      onClick={() => setConfirmReinicioAbierto(true)}
                    >
                      Reiniciar
                    </Button>
                  </Tooltip>
                </Group>
              )}
            </Group>

            {historial.length === 0 ? (
              <Text size="xs" ta="center" py="md" style={{ color: mutedColor }}>
                Aún no has marcado flashcards como estudiadas.
              </Text>
            ) : (
              <ScrollArea.Autosize mah={340} type="auto" offsetScrollbars>
              <Stack gap="xs">
                {historial.map((h) => (
                  <Card key={h.flashcardId} withBorder radius="md" p="xs" style={cardStyle}>
                    <Group justify="space-between" wrap="nowrap" gap="xs">
                      <Box style={{ minWidth: 0, flex: 1 }}>
                        <Text size="xs" lineClamp={2} fw={500} ta="left" style={{ color: textColor }}>
                          {h.pregunta}
                        </Text>
                        <Group gap={6} mt={2}>
                          {h.especialidadNombre && (
                            <Text size="10px" style={{ color: mutedColor }}>
                              {h.especialidadNombre}
                            </Text>
                          )}
                          <Text size="10px" style={{ color: mutedColor }}>·</Text>
                          <Text size="10px" style={{ color: mutedColor }}>
                            {tiempoRelativo(h.ultimaVezEstudiado)}
                          </Text>
                          {h.vecesEstudiado > 1 && (
                            <Badge size="xs" color="gray" variant="light">
                              ×{h.vecesEstudiado}
                            </Badge>
                          )}
                        </Group>
                      </Box>
                      <Tooltip label="Repasar esta">
                        <ActionIcon
                          variant="subtle"
                          size="sm"
                          color="gray"
                          onClick={() => handleRepasarUna(h.flashcardId)}
                          aria-label="Repasar esta"
                        >
                          <IconPlayerPlayFilled size={12} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  </Card>
                ))}
              </Stack>
              </ScrollArea.Autosize>
            )}
          </Paper>
        </Grid.Col>
      </Grid>

      {/* Confirmación de reinicio de flashcards (estilo del sistema, no el confirm nativo) */}
      <Modal
        opened={confirmReinicioAbierto}
        onClose={() => !reiniciando && setConfirmReinicioAbierto(false)}
        title={
          <Text
            fw={600}
            style={{
              color: textColor,
              fontFamily: 'Space Grotesk, Inter, sans-serif',
              fontSize: '18px',
            }}
          >
            Reiniciar flashcards
          </Text>
        }
        centered
        size="sm"
        overlayProps={{ opacity: 0.4, blur: 8 }}
        styles={{
          content: {
            backgroundColor: colorScheme === 'dark' ? 'rgba(30, 41, 59, 0.98)' : 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(10px)',
            border: `1px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
            borderRadius: '12px',
          },
          header: { backgroundColor: 'transparent', borderBottom: 'none', paddingBottom: 0 },
          body: { padding: '1rem' },
        }}
      >
        <Stack gap="md">
          <Text size="sm" style={{ color: mutedColor, fontFamily: 'Inter, sans-serif' }}>
            Todas tus tarjetas estudiadas volverán a contar como <b>nuevas</b>. Esta acción no se puede deshacer.
          </Text>
          <Group justify="flex-end" gap="sm">
            <Button
              variant="subtle"
              onClick={() => setConfirmReinicioAbierto(false)}
              disabled={reiniciando}
              style={{ color: mutedColor, fontFamily: 'Inter, sans-serif' }}
            >
              Cancelar
            </Button>
            <Button
              color="red"
              onClick={handleReiniciarProgreso}
              loading={reiniciando}
              leftSection={<IconRefresh size={14} />}
              style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600 }}
            >
              Reiniciar todas
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Box>
  );
};

export default FlashcardsPage;
